import React, { useCallback, useRef, useState } from 'react';

const ContactForm = ({
  formData,
  onChange,
  onSubmit,
  isSubmitting,
  pushToDataLayer,
  privacyLink,
  termsLink,
}) => {
  const [errors, setErrors] = useState({});
  const trackedFieldsRef = useRef({
    fullName: false,
    email: false,
  });

  const validateField = (name, value, formData) => {
    switch (name) {
      case 'fullName': {
        const trimmed = value.trim();
        const nameWithoutSpaces = trimmed.replace(/\s/g, '');

        if (!trimmed) return 'Name is required';
        if (nameWithoutSpaces.length < 3) return 'Name must be at least 3 characters';
        return '';
      }

      case 'email': {
        const trimmedEmail = value.trim();
        if (!trimmedEmail) return 'Email is required';
        if (trimmedEmail.length > 40) return 'Email must not exceed 40 characters';
        if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[A-Za-z]{2,}$/.test(trimmedEmail)) {
          return 'Please enter a valid email address';
        }
        return '';
      }

      case 'mobileNumber': {
        if (!value) return 'Mobile number is required';
        if (!/^\d{10}$/.test(value)) return 'Please enter a valid 10-digit mobile number';
        return '';
      }

      case 'agreeToTerms': {
        if (!formData.agreeToTerms) return 'You must agree to Privacy Policy & Terms';
        return '';
      }

      default:
        return '';
    }
  };

  const handleChange = useCallback(
    (e) => {
      if (!e.target || !e.target.name) return;
      const { name, value, type, checked } = e.target;

      let newValue = value;

      if (name === 'fullName') {
        newValue = value.replace(/[^a-zA-Z\s.'-]/g, '');
        newValue = newValue.replace(/^\s+/, '');
        if (newValue.length > 30) {
          newValue = newValue.slice(0, 30);
        }
      }

      if (name === 'email') {
        newValue = value.trimStart();
        if (newValue.length > 40) {
          newValue = newValue.slice(0, 40);
        }
      }

      if (name === 'mobileNumber') {
        newValue = value.replace(/\D/g, '');
        if (newValue.length > 10) {
          newValue = newValue.slice(0, 10);
        }
      }

      onChange({
        [name]: type === 'checkbox' ? checked : newValue,
      });

      const errorMessage = validateField(name, type === 'checkbox' ? checked : newValue, {
        ...formData,
        [name]: type === 'checkbox' ? checked : newValue,
      });

      setErrors((prev) => ({
        ...prev,
        [name]: errorMessage,
      }));

      const updatedFormData = {
        ...formData,
        [name]: type === 'checkbox' ? checked : newValue,
      };

      // Track when user enters contact details
      if (type === 'checkbox') {
        // Track checkbox (Privacy Policy & Terms)
        pushToDataLayer({
          event: 'Enquiry_Form',
          eventCategory: 'Enquiry form',
          eventAction: 'Authorization',
          eventLabel: checked ? 'Check' : 'Uncheck',
          FormType: 'ELV Form',
          section_name: 'Contact Details',
        });
      }

      const trimmedName = updatedFormData.fullName.trim();
      const validName = trimmedName.replace(/\s/g, '').length >= 3;

      if (name === 'fullName') {
        if (validName && !trackedFieldsRef.current.fullName) {
          pushToDataLayer({
            event: 'Enquiry_Form',
            eventCategory: 'Enquiry form',
            eventAction: 'Name',
            eventLabel: 'NA',
            FormType: 'ELV Form',
            section_name: 'Contact Details',
          });
          trackedFieldsRef.current.fullName = true;
        } else if (!validName) {
          trackedFieldsRef.current.fullName = false;
        }
      }

      const trimmedEmail = updatedFormData.email.trim();
      const validEmail = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[A-Za-z]{2,}$/.test(trimmedEmail);

      if (name === 'email') {
        if (validEmail && !trackedFieldsRef.current.email) {
          pushToDataLayer({
            event: 'Enquiry_Form',
            eventCategory: 'Enquiry form',
            eventAction: 'Mail',
            eventLabel: 'NA',
            FormType: 'ELV Form',
            section_name: 'Contact Details',
          });
          trackedFieldsRef.current.email = true;
        } else if (!validEmail) {
          trackedFieldsRef.current.email = false;
        }
      }

      // Track mobile when valid (10 digits)
      if (name === 'mobileNumber' && newValue.length === 10) {
        pushToDataLayer({
          event: 'Enquiry_Form',
          eventCategory: 'Enquiry form',
          eventAction: 'Mobile Number',
          eventLabel: 'NA',
          FormType: 'ELV Form',
          section_name: 'Contact Details',
        });
      }
    },
    [formData, onChange, pushToDataLayer]
  );

  const handleFormSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};
    const trimmedName = formData.fullName.trim();
    const nameWithoutSpaces = trimmedName.replace(/\s/g, '');
    const trimmedEmail = formData.email.trim();

    if (!trimmedName) {
      newErrors.fullName = 'Name is required';
    } else if (nameWithoutSpaces.length < 3) {
      newErrors.fullName = 'Name must be at least 3 characters';
    }

    if (!trimmedEmail) {
      newErrors.email = 'Email is required';
    } else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[A-Za-z]{2,}$/.test(trimmedEmail)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!/^\d{10}$/.test(formData.mobileNumber)) {
      newErrors.mobileNumber = 'Please enter a valid 10-digit mobile number';
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length === 0) {
      onSubmit(e);
    }
  };

  const trimmedName = formData.fullName?.trim() || '';
  const nameWithoutSpaces = trimmedName.replace(/\s/g, '');

  const isFormValid =
    trimmedName &&
    nameWithoutSpaces.length >= 3 &&
    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[A-Za-z]{2,}$/.test(formData.email?.trim()) &&
    /^[0-9]{10}$/.test(formData.mobileNumber) &&
    formData.agreeToTerms &&
    !isSubmitting;

  const handlePrivacyClick = () => {
    pushToDataLayer({
      event: 'Enquiry_Form',
      eventCategory: 'Enquiry form',
      eventAction: 'Privacy Policy',
      eventLabel: 'NA',
      FormType: 'ELV Form',
      section_name: 'Contact Details',
    });
  };

  const handleTermsClick = () => {
    pushToDataLayer({
      event: 'Enquiry_Form',
      eventCategory: 'Enquiry form',
      eventAction: 'Terms & Conditions',
      eventLabel: 'NA',
      FormType: 'ELV Form',
      section_name: 'Contact Details',
    });
  };

  return (
    <form className="contact-form" onSubmit={handleFormSubmit}>
      <div className="form-group floating-label">
        <input
          type="text"
          id="fullName"
          name="fullName"
          value={formData.fullName}
          onChange={handleChange}
          placeholder="Full Name"
          disabled={isSubmitting}
        />
        <label htmlFor="fullName">
          Full Name<span className="required">*</span>
        </label>
        {errors.fullName && <p className="field-error">{errors.fullName}</p>}
      </div>

      <div className="form-group floating-label">
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="Email"
          maxLength="40"
          disabled={isSubmitting}
        />
        <label htmlFor="email">
          Email<span className="required">*</span>
        </label>
        {errors.email && <p className="field-error">{errors.email}</p>}
      </div>

      <div className="form-group floating-label">
        <input
          type="tel"
          id="mobileNumber"
          name="mobileNumber"
          value={formData.mobileNumber}
          onChange={handleChange}
          className="mobile-number"
          placeholder="Mobile Number"
          inputMode="numeric"
          maxLength="10"
          disabled={isSubmitting}
        />
        <label htmlFor="mobileNumber">
          Mobile Number<span className="required">*</span>
        </label>
        {errors.mobileNumber && <p className="field-error">{errors.mobileNumber}</p>}
      </div>

      <div className="checkbox-group">
        <input
          type="checkbox"
          id="agreeToTerms"
          name="agreeToTerms"
          checked={formData.agreeToTerms}
          onChange={handleChange}
          disabled={isSubmitting}
        />
        <label htmlFor="agreeToTerms" className="checkbox-label">
          By verifying with OTP, I confirm that I have read and understood the{' '}
          <a
            href={privacyLink || '#'}
            className="link"
            target="_blank"
            rel="noopener noreferrer"
            onClick={handlePrivacyClick}
          >
            Privacy Policy
          </a>{' '}
          and agree to the{' '}
          <a
            href={termsLink || '#'}
            className="link"
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleTermsClick}
          >
            Terms & Conditions
          </a>
        </label>
      </div>

      <button
        type="submit"
        className={`submit-button ${isFormValid ? 'enabled' : ''}`}
        disabled={!isFormValid}
      >
        {isSubmitting ? 'Sending...' : 'Send OTP'}
      </button>
    </form>
  );
};

export default ContactForm;
